import React, { useState, useEffect } from 'react';
import restaurantAPI from '../../api/restaurantAPI';

export const AddTableModal = ({
  isOpen,
  onClose,
  onTableCreated,
  loading: externalLoading,
  existingSections = [],
}) => {
  const [tableNumber, setTableNumber] = useState('');
  const [section, setSection] = useState('');
  const [seats, setSeats] = useState('4');
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldError, setFieldError] = useState(null); // for duplicate name

  useEffect(() => {
    if (!isOpen) {
      setTableNumber('');
      setSection('');
      setSeats('4');
      setError(null);
      setFieldError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = tableNumber.trim();
    if (!name) {
      setFieldError('Table number / name is required');
      return;
    }

    setError(null);
    setFieldError(null);
    setLocalLoading(true);

    try {
      const res = await restaurantAPI.tables.create({
        tableNumber: name,
        seats: parseInt(seats) || 4,
        section: section.trim() || 'indoor',
      });

      const created = res?.data?.table || res?.table;
      onTableCreated?.(created);
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message || 'Failed to create table';

      if (status === 409) {
        setFieldError(`Table "${tableNumber.trim()}" already exists`);
      } else {
        setError(message);
      }
    } finally {
      setLocalLoading(false);
    }
  };

  const busy = localLoading || externalLoading;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-xs rounded-xl bg-white p-5 shadow-2xl">
        <h3 className="text-lg font-bold text-gray-900">Add New Table</h3>
        <p className="mt-1 text-xs text-gray-500">Table will be immediately available for selection</p>

        {error && (
          <div className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Table Number / Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                fieldError ? 'border-red-400' : 'border-gray-300'
              }`}
              placeholder="e.g. 11, Bar 2, VIP 1"
              value={tableNumber}
              onChange={(e) => { setTableNumber(e.target.value); setFieldError(null); }}
              autoFocus
              disabled={busy}
            />
            {fieldError && (
              <p className="mt-1 text-xs text-red-600">{fieldError}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Section</label>
            <input
              type="text"
              list="section-suggestions"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. indoor, outdoor, bar"
              value={section}
              onChange={(e) => setSection(e.target.value)}
              disabled={busy}
            />
            <datalist id="section-suggestions">
              {existingSections.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Seats</label>
            <input
              type="number"
              min="1"
              max="20"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              disabled={busy}
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !tableNumber.trim()}
              className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Creating…' : 'Create Table'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTableModal;
